import React, { useContext, useMemo } from 'react';
import { motion } from 'framer-motion';
import { ThemeContext } from '../ThemeContext';
import { useAnnouncementBar } from '../components/AnnouncementBarContext';
import { Shield, Database, Eye, Share2, Trash2, Baby } from 'lucide-react';

// Module-level animation state tracking
const cardAnimationState = new Map();

// Card component moved outside to prevent re-creation
const Card = ({ children, className = '', delay = 0, cardId }) => {
  const hasAnimated = cardAnimationState.has(cardId) ? cardAnimationState.get(cardId) : false;

  React.useEffect(() => {
    if (!hasAnimated) {
      cardAnimationState.set(cardId, true);
    }
  }, [cardId, hasAnimated]);

  return (
    <motion.div
      initial={hasAnimated ? false : { opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.6,
        delay: hasAnimated ? 0 : delay,
        type: "spring",
        stiffness: 100,
      }}
      whileHover={{
        scale: 1.02,
        y: -5,
        transition: { duration: 0.2 },
      }}
      className={`relative backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-3xl shadow-2xl p-6 md:p-8 border border-white/20 dark:border-gray-700/50 ${className}`}
      style={{
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      }}
    >
      {children}
    </motion.div>
  );
};

const PrivacyPage = () => {
  const { isDarkMode } = useContext(ThemeContext);
  const { isVisible: isAnnouncementVisible, height: announcementHeight } = useAnnouncementBar();
  const fixedHeaderOffset = (isAnnouncementVisible ? announcementHeight : 0) + 80;

  // Memoize title gradient to update when dark mode changes
  const titleGradient = useMemo(() => {
    const color = isDarkMode ? '#ff7a00' : '#212D82';
    return `linear-gradient(90deg, ${color} 0%, ${color} 40%, rgba(255, 122, 0, 0.8) 50%, ${color} 60%, ${color} 100%)`;
  }, [isDarkMode]);

  const dataCategories = [
    {
      category: 'Personal Identifiers',
      data: 'Name, Email Address, Password (encrypted).',
      purpose: 'Account creation, login, subscription management, and direct communication (e.g., score reports).',
    },
    {
      category: 'Payment Information',
      data: 'Billing address, last four digits of card, subscription term.',
      purpose: 'Processing subscription fees (handled securely by a third-party payment processor, not stored directly by us).',
    },
    {
      category: 'Academic & Performance Data',
      data: 'Practice scores, time per question, content mastery levels, quiz attempts, initial diagnostic score.',
      purpose: 'Powering the adaptive learning engine, personalized feedback, and generating Rank Up Hub leaderboards.',
    },
    {
      category: 'Competitive & Social Data',
      data: 'Rank (current and historical), Competitive Challenge history, Discord username (if linked), Rank Zero Legend status.',
      purpose: 'Delivering the core competitive experience, validating contest integrity, and driving social engagement.',
    },
    {
      category: 'Device & Usage Data',
      data: 'IP address, device ID, operating system, pages/features viewed, referring URL.',
      purpose: 'Troubleshooting, security, understanding feature usage, and improving the Service (B2B and B2C analytics).',
    },
  ];

  return (
    <div
      className="min-h-screen pb-24 pl-0 md:pl-24 overflow-hidden bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900"
      style={{ paddingTop: `${fixedHeaderOffset}px` }}
    >
      <div className="relative z-20 px-4 sm:px-6 lg:px-8 py-6 md:py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: "spring" }}
          className="text-center mb-8 md:mb-12"
        >
          <h1
            key={`privacy-title-${isDarkMode}`}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold font-zonaProUltra mb-4 relative inline-block"
            style={{
              background: titleGradient,
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'shine 10s linear infinite',
              lineHeight: '1.2',
              paddingBottom: '0.25rem'
            }}
          >
            Privacy Policy
          </h1>
          <motion.p
            className="text-gray-600 dark:text-gray-400 text-base sm:text-lg md:text-xl font-zonaProRegular"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Privacy Policy for Game Your SAT
          </motion.p>
          <motion.p
            className="text-gray-500 dark:text-gray-500 text-sm md:text-base font-zonaProRegular mt-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            Last Updated: November 10, 2025
          </motion.p>
          <motion.p
            className="text-gray-600 dark:text-gray-400 text-sm md:text-base font-zonaProRegular mt-4 max-w-3xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            This Privacy Policy explains how METY Technology, Inc. ("we," "us," or "our") collects, uses, discloses, and safeguards your information when you use the Game Your SAT application (the "Service").
          </motion.p>
        </motion.div>

        <div className="max-w-4xl mx-auto space-y-6 md:space-y-8">
          {/* Section 1: Information We Collect */}
          <Card delay={0.2} cardId="privacy-1">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 rounded-xl bg-[#06bae4]/10 dark:bg-[#06bae4]/20">
                  <Database size={24} className="text-[#06bae4]" />
                </div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold font-zonaProUltra text-gray-800 dark:text-gray-200">
                  1. Information We Collect
                </h2>
              </div>
              <p className="text-base md:text-lg text-gray-700 dark:text-gray-300 font-zonaProRegular leading-relaxed mb-6 text-left">
                We collect information primarily to deliver the personalized, competitive features of the Service, process subscriptions, and improve academic outcomes.
              </p>
              
              {/* Data Categories Table */}
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b-2 border-gray-300 dark:border-gray-600">
                      <th className="text-left py-3 px-4 text-sm font-bold font-zonaProUltra text-gray-800 dark:text-gray-200">Category</th>
                      <th className="text-left py-3 px-4 text-sm font-bold font-zonaProUltra text-gray-800 dark:text-gray-200">Data Collected</th>
                      <th className="text-left py-3 px-4 text-sm font-bold font-zonaProUltra text-gray-800 dark:text-gray-200">Purpose</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dataCategories.map((item, index) => (
                      <motion.tr
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.5 + index * 0.1 }}
                        className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                      >
                        <td className="py-4 px-4 text-sm md:text-base font-semibold font-zonaProUltra text-gray-800 dark:text-gray-200">{item.category}</td>
                        <td className="py-4 px-4 text-sm md:text-base font-zonaProRegular text-gray-700 dark:text-gray-300">{item.data}</td>
                        <td className="py-4 px-4 text-sm md:text-base font-zonaProRegular text-gray-700 dark:text-gray-300">{item.purpose}</td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </Card>

          {/* Section 2: How We Use Your Information */}
          <Card delay={0.3} cardId="privacy-2">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-xl bg-[#06bae4]/10 dark:bg-[#06bae4]/20">
                  <Eye size={24} className="text-[#06bae4]" />
                </div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold font-zonaProUltra text-gray-800 dark:text-gray-200">
                  2. How We Use Your Information
                </h2>
              </div>
              <p className="text-base md:text-lg text-gray-700 dark:text-gray-300 font-zonaProRegular leading-relaxed mb-4 text-left">
                We use the collected data for the following purposes:
              </p>
              <ul className="space-y-3 text-base md:text-lg text-gray-700 dark:text-gray-300 font-zonaProRegular leading-relaxed list-disc list-outside ml-6 pl-2 text-left">
                <li className="text-left"><strong className="font-zonaProUltra">Service Delivery:</strong> To operate and maintain your access to the Game Your SAT features, including synchronized progress across devices.</li>
                <li className="text-left"><strong className="font-zonaProUltra">Personalization:</strong> To tailor the content, difficulty, and study recommendations provided by the adaptive learning engine.</li>
                <li className="text-left"><strong className="font-zonaProUltra">Monetization & Transactions:</strong> To process payments for the Monthly and Annual Subscriptions and manage billing cycles.</li>
                <li className="text-left"><strong className="font-zonaProUltra">Communication:</strong> To send essential account updates, Rank-Up Competition notifications, and personalized score reports (via HubSpot automation).</li>
                <li className="text-left"><strong className="font-zonaProUltra">Research & Improvement (De-identified):</strong> To analyze user behavior in the aggregate (without personal identification) to improve our algorithms and the Engage IQ Platform for B2B partners.</li>
                <li className="text-left"><strong className="font-zonaProUltra">Security:</strong> To detect and prevent fraud, cheating, and unauthorized access to the Service.</li>
              </ul>
            </motion.div>
          </Card>

          {/* Section 3: Sharing and Disclosure */}
          <Card delay={0.4} cardId="privacy-3">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-xl bg-[#06bae4]/10 dark:bg-[#06bae4]/20">
                  <Share2 size={24} className="text-[#06bae4]" />
                </div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold font-zonaProUltra text-gray-800 dark:text-gray-200">
                  3. Sharing and Disclosure of Information
                </h2>
              </div>
              <p className="text-base md:text-lg text-gray-700 dark:text-gray-300 font-zonaProRegular leading-relaxed mb-4 text-left">
                We do not sell your personal data. We share information only in the following limited circumstances:
              </p>
              <ul className="space-y-3 text-base md:text-lg text-gray-700 dark:text-gray-300 font-zonaProRegular leading-relaxed list-disc list-outside ml-6 pl-2 text-left">
                <li className="text-left"><strong className="font-zonaProUltra">Service Providers:</strong> We share necessary data with third parties (e.g., HubSpot for email, Stripe/PayPal for payment processing, cloud hosting) who perform services on our behalf under strict confidentiality agreements.</li>
                <li className="text-left"><strong className="font-zonaProUltra">Public Leaderboards:</strong> Your chosen username and your competitive Rank/Score may be publicly displayed on the Rank Up Hub leaderboards and in the app interface.</li>
                <li className="text-left"><strong className="font-zonaProUltra">B2B Partners (Engage IQ Platform):</strong> If you access the Service through a licensing partner (e.g., a local tutoring center), that partner will have access to your performance data, mastery levels, and time spent via the Instructor Dashboard.</li>
                <li className="text-left"><strong className="font-zonaProUltra">Legal Compliance:</strong> When required by law, such as to comply with a subpoena or similar legal process.</li>
              </ul>
            </motion.div>
          </Card>

          {/* Section 4: Data Retention and Your Rights */}
          <Card delay={0.5} cardId="privacy-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-xl bg-[#06bae4]/10 dark:bg-[#06bae4]/20">
                  <Trash2 size={24} className="text-[#06bae4]" />
                </div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold font-zonaProUltra text-gray-800 dark:text-gray-200">
                  4. Data Retention and Your Rights
                </h2>
              </div>
              <div className="space-y-4 text-base md:text-lg text-gray-700 dark:text-gray-300 font-zonaProRegular leading-relaxed text-left">
                <div className="text-left">
                  <strong className="font-zonaProUltra">Retention:</strong> We retain your personal information only for as long as necessary to provide the Service, maintain necessary business records, and comply with our legal obligations.
                </div>
                <div className="text-left">
                  <strong className="font-zonaProUltra">Your Rights:</strong> You have the right to review, update, or request the deletion of your account and personal data, subject to certain legal exceptions.
                </div>
              </div>
            </motion.div>
          </Card>

          {/* Section 5: Children's Privacy */}
          <Card delay={0.6} cardId="privacy-5">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-xl bg-[#06bae4]/10 dark:bg-[#06bae4]/20">
                  <Baby size={24} className="text-[#06bae4]" />
                </div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold font-zonaProUltra text-gray-800 dark:text-gray-200">
                  5. Children's Privacy (COPPA Compliance)
                </h2>
              </div>
              <p className="text-base md:text-lg text-gray-700 dark:text-gray-300 font-zonaProRegular leading-relaxed text-left">
                The Service is intended for high school students preparing for the SAT (typically ages 14+). We comply with the Children's Online Privacy Protection Act (COPPA). We do not knowingly collect personal information from children under the age of 13 without obtaining their parents' consent. If we learn we have inadvertently collected information from a user under 13 without verifiable parental consent, we will take steps to delete the information promptly.
              </p>
            </motion.div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
